import { PageHero } from "@/components/PageHero";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, getSessionId } from "@/lib/api";
import { Link } from "react-router-dom";
import { Activity, Printer, Trash2, Plus, LifeBuoy, MessageCircle, CheckCircle2 } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";
import { toast } from "sonner";
import DripWelcomeBanner from "@/components/DripWelcomeBanner";
import useDripReferral from "@/hooks/useDripReferral";

const BEHAVIOR_TYPES = [
    "Agitation", "Wandering", "Sleep problems", "Refusal of care",
    "Hallucinations", "Paranoia", "Aggression", "Appetite changes", "Other",
];

const TIMES = ["Morning", "Afternoon", "Evening", "Night"];

/**
 * Quick-add chips shown on the Day 7 drip-landing experience.
 *
 * Each chip maps to a real BEHAVIOR_TYPES value + optional overrides
 * (time-of-day forced to a specific bucket, extra note pre-filled).
 *
 * Rules from the Day 7 spec (P1):
 *   - Repetition → "Other" + note "Repetitive questions or statements"
 *   - Sundowning → "Agitation" + time_of_day forced to "Evening"
 *   - Refused meds → "Refusal of care"
 *   - Fall → "Other" + note "Fall"
 * Wandering + Sleep already have exact BEHAVIOR_TYPES matches.
 */
const QUICK_ADD_CHIPS = [
    { label: "Repetition",   behavior_type: "Other",           note: "Repetitive questions or statements" },
    { label: "Wandering",    behavior_type: "Wandering",       note: "" },
    { label: "Sundowning",   behavior_type: "Agitation",       note: "Sundowning — late-afternoon/evening agitation",
        force_time: "Evening" },
    { label: "Refused meds", behavior_type: "Refusal of care", note: "Refused medication" },
    { label: "Sleep",        behavior_type: "Sleep problems",  note: "" },
    { label: "Fall",         behavior_type: "Other",           note: "Fall" },
];

/**
 * Map the current wall-clock hour to the tracker's time-of-day bucket.
 * Kept in sync with TIMES above.
 */
function currentTimeBucket() {
    const h = new Date().getHours();
    if (h >= 6 && h <= 11) return "Morning";
    if (h >= 12 && h <= 16) return "Afternoon";
    if (h >= 17 && h <= 20) return "Evening";
    return "Night";
}

const EMPTY_FORM = {
    behavior_type: "", time_of_day: "", trigger: "",
    what_helped: "", what_made_worse: "", severity: 3, notes: "",
};

export default function Behavior() {
    const sid = getSessionId();
    // Shared drip-landing hook — reads ?ref, POSTs the click, strips the
    // querystring, and gives us the caregiver's loved-one name.
    const drip = useDripReferral();
    const [form, setForm] = useState(EMPTY_FORM);
    const [logs, setLogs] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    // Post-save inline confirmation. Cleared as soon as the caregiver
    // starts typing a new entry so it doesn't linger past its welcome.
    const [savedOnce, setSavedOnce] = useState(false);
    const saveBtnRef = useRef(null);

    // ------------ Load recent logs ------------
    useEffect(() => { loadLogs(); }, []);
    const loadLogs = async () => {
        try {
            const r = await api.get(`/behavior/${sid}`);
            setLogs(r.data);
        } catch (_e) { /* offline / auth transient — silent */ }
    };

    // ------------ Quick-add chip → prefill form + scroll to save ------------
    const applyQuickChip = (chip) => {
        setForm(prev => ({
            ...prev,
            behavior_type: chip.behavior_type,
            severity: 3,
            time_of_day: chip.force_time || currentTimeBucket(),
            // If the chip has a note, merge it into whatever the user
            // already typed. Rare that they'd have typed anything since
            // chips are the fast path — but be gentle if they did.
            notes: chip.note
                ? (prev.notes ? `${prev.notes}\n${chip.note}` : chip.note)
                : prev.notes,
        }));
        setSavedOnce(false); // clear confirmation if it was up
        // Analytics for the drip-landing funnel.
        try {
            track("drip_quick_chip_tap", {
                kind: drip.kind || "no_drip",
                chip: chip.label,
            });
        } catch (_e) { /* best-effort */ }
        // Server-side tracking too, but ONLY when the caregiver arrived
        // from a drip (drip.kind set). Otherwise chip taps that happen
        // outside a drip flow shouldn't inflate the funnel numbers.
        if (drip.kind) {
            api.post("/me/drip-chip-tap", { kind: drip.kind, chip: chip.label })
               .catch(() => { /* best-effort — analytics never blocks UX */ });
        }
        // Scroll the Save button into view (form is tall). Timeout gives
        // React a tick to re-render the pre-selected chip.
        setTimeout(() => {
            if (saveBtnRef.current) {
                saveBtnRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 80);
    };

    // ------------ Save ------------
    const submit = async (e) => {
        e.preventDefault();
        if (!form.behavior_type || !form.time_of_day) {
            toast.error("Pick a behavior and time of day.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/behavior", { session_id: sid, ...form });
            track(EVENTS.BEHAVIOR_LOG_ADDED, { behavior_type: form.behavior_type, time_of_day: form.time_of_day });
            // Attribute the save to the drip if the banner was showing —
            // gives us the funnel step "drip click → behavior logged".
            if (drip.kind) {
                try {
                    track("drip_behavior_logged", { kind: drip.kind, behavior_type: form.behavior_type });
                } catch (_e) { /* best-effort */ }
            }
            setForm(EMPTY_FORM);
            await loadLogs();
            setSavedOnce(true);
            toast.success("Logged.");
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (id) => {
        await api.delete(`/behavior/${id}`);
        await loadLogs();
    };

    const printSummary = () => window.print();

    // ------------ Derived copy ------------
    const bannerVisible = !!drip.kind && !drip.dismissed;
    const bannerMessage = useMemo(() => {
        const lo = drip.lovedOneName || "your loved one";
        // Day 14 email talks about patterns; Day 7 is the classic weekly.
        // Weekly post-28 email swings between "log a moment" and "nothing
        // to log this week? that's okay." — keep the framing gentle.
        if (drip.kind === "day14") {
            return `Two weeks in. Anything new or shifting with ${lo}? Even one moment helps Lane see the pattern.`;
        }
        if (drip.kind === "post28_weekly") {
            return `Lane's weekly check-in. Anything worth noting with ${lo} this week? Even "nothing changed" is useful.`;
        }
        return `Lane's weekly check-in. What happened with ${lo} this week? One tap is all it takes.`;
    }, [drip.kind, drip.lovedOneName]);

    return (
        <div data-clarity-mask="true" className="space-y-10 animate-fade-in-up">

            {/* -------------------- Drip landing banner + quick chips --------------------
                Shown when the caregiver arrives from Day 7 / Day 14 / weekly
                post-28 emails. The banner copy is dimension-specific (Day 14
                talks about patterns; weekly talks about "nothing to log is
                fine too"). Quick chips are always the same six because they
                map to the highest-leverage caregiver behaviors we see logged. */}
            {bannerVisible && (
                <DripWelcomeBanner
                    kind={drip.kind}
                    testid="drip-welcome-banner"
                    message={bannerMessage}
                    onDismiss={drip.dismiss}
                >
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-2">
                        One tap · pre-fills the form below
                    </p>
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 gap-2"
                        data-testid="drip-quick-chips"
                    >
                        {QUICK_ADD_CHIPS.map(chip => (
                            <button
                                key={chip.label}
                                type="button"
                                data-testid={`drip-quick-chip-${chip.label.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => applyQuickChip(chip)}
                                className="text-left text-[15px] font-semibold text-[#1F2624] bg-white border border-cream-dark hover:border-teal-dark hover:bg-teal-soft/50 rounded-2xl px-4 py-3.5 transition-colors min-h-[52px] shadow-sm"
                            >
                                {chip.label}
                            </button>
                        ))}
                    </div>
                </DripWelcomeBanner>
            )}

            <PageHero
                className="no-print"
                kicker="Behavior"
                kickerIcon={Activity}
                title="Behavior tracker"
                subtitle={<>Log what you notice. Over time you&apos;ll see patterns — and have a clear summary to share with the doctor.</>}
                actions={
                    <button onClick={printSummary} data-testid="behavior-print" className="ml-btn-secondary !min-h-[44px] !px-4">
                        <Printer size={18} strokeWidth={1.6} /> Print summary
                    </button>
                }
            />

            {/* Guidance link */}
            <Link to="/guidance" data-testid="behavior-to-guidance"
                className="ml-card ml-card-hover p-5 md:p-6 bg-ml_blue-light !border-ml_blue-light flex items-center gap-4 no-print">
                <span className="w-11 h-11 rounded-2xl bg-white text-ml_blue-dark flex items-center justify-center shrink-0">
                    <LifeBuoy strokeWidth={1.6} size={20} />
                </span>
                <div className="flex-1">
                    <h3 className="font-heading font-medium text-ml_blue-dark text-lg">Need help in the moment?</h3>
                    <p className="text-sm text-[#1F2624]">
                        Open Behavior Guidance for what to try, what to avoid, and late-stage strategies.
                    </p>
                </div>
                <span className="text-ml_blue-dark font-medium hidden sm:inline">Open →</span>
            </Link>

            {/* Post-save inline confirmation. Sits ABOVE the form so the
                caregiver sees it immediately without needing to scroll. */}
            {savedOnce && (
                <section
                    data-testid="behavior-saved-confirmation"
                    className="ml-card p-4 md:p-5 border-l-4 border-l-teal-dark bg-teal-soft/40 flex items-start justify-between gap-4 flex-wrap"
                >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <CheckCircle2 size={20} strokeWidth={1.8} className="text-teal-dark shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-[#1F2624]">Logged. Lane will use this next time you chat.</p>
                            <p className="text-sm text-[#4C5551] mt-0.5">
                                Add another below, or take this to Lane for context.
                            </p>
                        </div>
                    </div>
                    <Link
                        to="/chat"
                        data-testid="behavior-saved-to-lane"
                        className="inline-flex items-center gap-2 bg-teal-dark text-white hover:bg-teal transition rounded-2xl px-4 py-2 text-sm font-semibold shrink-0"
                    >
                        <MessageCircle size={14} strokeWidth={1.8} />
                        Talk to Lane about this →
                    </Link>
                </section>
            )}

            {/* Logging form */}
            <form onSubmit={submit} className="ml-card p-6 md:p-8 space-y-5 no-print" data-testid="behavior-form">
                <h2 className="text-2xl font-heading font-medium text-teal flex items-center gap-2">
                    <Plus size={20} strokeWidth={1.6} /> New entry
                </h2>

                <div>
                    <label className="ml-label">What happened?</label>
                    <div className="flex flex-wrap gap-2">
                        {BEHAVIOR_TYPES.map(b => (
                            <button
                                key={b}
                                type="button"
                                data-testid={`behavior-type-${b.replace(/\s+/g, '-').toLowerCase()}`}
                                onClick={() => { setForm({ ...form, behavior_type: b }); setSavedOnce(false); }}
                                className={`px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${form.behavior_type === b
                                    ? "bg-teal text-white border-teal"
                                    : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                    }`}
                            >{b}</button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="ml-label">Time of day</label>
                    <div className="flex flex-wrap gap-2">
                        {TIMES.map(t => (
                            <button
                                key={t}
                                type="button"
                                data-testid={`behavior-time-${t.toLowerCase()}`}
                                onClick={() => { setForm({ ...form, time_of_day: t }); setSavedOnce(false); }}
                                className={`px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${form.time_of_day === t
                                    ? "bg-teal text-white border-teal"
                                    : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                    }`}
                            >{t}</button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="ml-label">How intense? (1 mild — 5 severe)</label>
                    <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(v => (
                            <button
                                key={v}
                                type="button"
                                data-testid={`behavior-severity-${v}`}
                                onClick={() => { setForm({ ...form, severity: v }); setSavedOnce(false); }}
                                className={`flex-1 py-3 rounded-xl text-base font-medium border transition-all ${form.severity === v
                                    ? "bg-teal text-white border-teal"
                                    : "bg-white border-[#E2E8F0] text-[#4C5551] hover:bg-cream-dark"
                                    }`}
                            >{v}</button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="ml-label" htmlFor="trigger">Possible trigger</label>
                        <input id="trigger" data-testid="behavior-trigger" className="ml-input"
                            value={form.trigger} onChange={e => { setForm({ ...form, trigger: e.target.value }); setSavedOnce(false); }}
                            placeholder="e.g. Loud TV, missed meal" />
                    </div>
                    <div>
                        <label className="ml-label" htmlFor="helped">What helped</label>
                        <input id="helped" data-testid="behavior-helped" className="ml-input"
                            value={form.what_helped} onChange={e => { setForm({ ...form, what_helped: e.target.value }); setSavedOnce(false); }}
                            placeholder="e.g. A walk, quiet music" />
                    </div>
                    <div>
                        <label className="ml-label" htmlFor="worse">What made it worse</label>
                        <input id="worse" data-testid="behavior-worse" className="ml-input"
                            value={form.what_made_worse} onChange={e => { setForm({ ...form, what_made_worse: e.target.value }); setSavedOnce(false); }}
                            placeholder="e.g. Crowds, hunger" />
                    </div>
                    <div>
                        <label className="ml-label" htmlFor="notes">Notes</label>
                        <input id="notes" data-testid="behavior-notes" className="ml-input"
                            value={form.notes} onChange={e => { setForm({ ...form, notes: e.target.value }); setSavedOnce(false); }}
                            placeholder="Anything else?" />
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        ref={saveBtnRef}
                        type="submit"
                        disabled={submitting}
                        data-testid="behavior-submit"
                        className="ml-btn-primary disabled:opacity-50"
                    >
                        {submitting ? "Saving…" : "Save entry"}
                    </button>
                </div>
            </form>

            {/* Summary list */}
            <section className="ml-card p-6 md:p-8" data-testid="behavior-history">
                <h2 className="text-2xl font-heading font-medium text-teal mb-1">Recent entries</h2>
                <p className="text-sm text-[#4C5551] mb-5 no-print">Bring this to your next appointment.</p>

                {logs.length === 0 ? (
                    <p className="text-[#4C5551]">No entries yet. Your first one is just above.</p>
                ) : (
                    <ul className="space-y-4">
                        {logs.map(l => (
                            <li key={l.id} className="border border-[#E2E8F0] rounded-xl p-4 md:p-5" data-testid={`behavior-item-${l.id}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <span className="font-heading font-medium text-[#1F2624]">{l.behavior_type}</span>
                                        <span className="text-xs px-2.5 py-1 rounded-2xl bg-cream-dark text-[#4C5551]">{l.time_of_day}</span>
                                        <span className="text-xs px-2.5 py-1 rounded-2xl bg-ml_blue-light text-ml_blue-dark">Severity {l.severity}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-[#4C5551]">{new Date(l.created_at).toLocaleString()}</span>
                                        <button onClick={() => remove(l.id)} className="text-coral-dark hover:text-coral no-print" data-testid={`behavior-delete-${l.id}`}>
                                            <Trash2 size={16} strokeWidth={1.6} />
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-[#4C5551]">
                                    {l.trigger && <div><strong className="text-[#1F2624]">Trigger:</strong> {l.trigger}</div>}
                                    {l.what_helped && <div><strong className="text-[#1F2624]">Helped:</strong> {l.what_helped}</div>}
                                    {l.what_made_worse && <div><strong className="text-[#1F2624]">Worse:</strong> {l.what_made_worse}</div>}
                                </div>
                                {l.notes && <p className="mt-2 text-sm text-[#4C5551]">{l.notes}</p>}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
